/**
 * Karte eines Anleitungs-Entwurfs (KNOWLEDGE_ARTICLE): KIAN hat aus dem Gespräch eine Anleitung
 * für die Wissensdatenbank geschrieben — etwa, wie ein Problem per PowerShell gelöst wurde.
 *
 * Der Techniker liest sie hier, lässt sie im Chat verbessern („Schritt 3 genauer") oder ändert sie
 * selbst, und reicht sie ein. Veröffentlicht wird erst nach der Freigabe durch einen Admin; bis
 * zum Klick auf „Zur Freigabe einreichen" steht nichts in der Wissensdatenbank.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Anchor, Badge, Box, Button, Group, Loader, Modal, Paper, Stack, Text, ThemeIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { IconBook2, IconEdit, IconExternalLink, IconSend, IconTrash } from '@tabler/icons-react';
import { ApiError } from '../../../lib/api/http';
import type { KianAction, KianDraftIssue, KianKnowledgeDraft } from '../../../lib/api/domains/kianActions';
import { KnowledgeBaseMarkdown } from '../../knowledgeBase/KnowledgeBaseMarkdown';
import { KbScopeBadges } from '../../knowledgeBase/KbCustomerScopeField';
import { KianKnowledgeArticleEditorModal } from './KianKnowledgeArticleEditorModal';
import { ACTION_STATUS_COLOR, ACTION_STATUS_LABEL, actionSubtitle, executeLabel } from './kianActionUi';
import { kianActionKey, type useKianAction } from './useKianAction';

type Controls = Pick<ReturnType<typeof useKianAction>, 'update' | 'execute' | 'discard'>;

/** Feste Referenz: der Dialog setzt seinen Stand zurück, wenn sich die Kundenliste ändert */
const NO_CUSTOMERS: NonNullable<KianAction['items'][number]['articleResolved']>['customers'] = [];

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.summary + (err.detail ? ` — ${err.detail}` : '');
  return err instanceof Error ? err.message : 'Unbekannter Fehler';
}

export function KianKnowledgeArticleCard({
  action,
  controls,
  compact = false,
  readOnly = false,
}: {
  action: KianAction;
  controls: Controls;
  compact?: boolean;
  /** Archiviertes Gespräch: nur lesen, Einreichen und Ändern erst nach dem Reaktivieren */
  readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const { update, execute, discard } = controls;
  const [editing, setEditing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const item = action.items[0];
  const article = item?.article;
  if (!item || !article) {
    return (
      <Alert color="red" variant="light" mt="xs">
        Der Anleitungs-Entwurf ist unvollständig und lässt sich nicht anzeigen.
      </Alert>
    );
  }
  const resolved = item.articleResolved ?? null;
  const busy = update.isPending || execute.isPending || discard.isPending;
  const pending = action.status === 'PROPOSED' || action.status === 'FAILED';
  const open = pending && !readOnly;
  const errors = item.issues.filter((i) => i.level === 'error');
  const hints = item.issues.filter((i) => i.level !== 'error');
  const resolvedCustomers = resolved?.customers ?? NO_CUSTOMERS;
  const customers = article.scope === 'CUSTOMERS' ? resolvedCustomers : NO_CUSTOMERS;

  const patch = (draft: Partial<KianKnowledgeDraft>, after?: () => void) =>
    update.mutate(
      { items: [{ id: item.id, draft }] },
      {
        onSuccess: () => after?.(),
        onError: (err) => notifications.show({ color: 'red', title: 'Änderung nicht übernommen', message: errorMessage(err) }),
      },
    );

  const submit = () =>
    execute.mutate(undefined, {
      onError: (err) => {
        // Der Server hat die Prüfung frisch gemacht — die Karte soll zeigen, was jetzt fehlt
        void qc.invalidateQueries({ queryKey: kianActionKey(action.id), exact: true });
        notifications.show({ color: 'red', title: 'Nicht eingereicht', message: errorMessage(err) });
      },
    });

  /** Mehrdeutig genannter Kunde: die Auswahl ersetzt die Nennung. */
  const pickCustomer = (issue: KianDraftIssue, candidateId: string) =>
    patch({
      scope: 'CUSTOMERS',
      customerIds: [...new Set([...article.customerIds, candidateId])],
      customerRefs: article.customerRefs.filter((r) => r !== issue.mapKey),
    });

  const articleHref = resolved?.articleSlug ? `/knowledge-base/${encodeURIComponent(resolved.articleSlug)}` : null;
  const byLine =
    article.revisedBy === 'USER'
      ? 'zuletzt von dir geändert'
      : article.revision > 1
        ? `von KIAN überarbeitet${article.changeNote ? ` — ${article.changeNote}` : ''}`
        : 'von KIAN geschrieben';

  return (
    <Paper withBorder radius="md" p="sm" mt="xs" data-kian-action={action.id}>
      <Stack gap="xs">
        <Group justify="space-between" wrap="wrap" align="flex-start" gap="xs">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: '1 1 12rem' }}>
            <ThemeIcon variant="light" color={ACTION_STATUS_COLOR[action.status]} size="md" radius="md">
              <IconBook2 size={16} />
            </ThemeIcon>
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={2}>
                {article.title || 'Anleitung ohne Titel'}
              </Text>
              <Text size="xs" c="dimmed">
                {actionSubtitle(action)}
              </Text>
            </Stack>
          </Group>
          <Badge variant="light" color={ACTION_STATUS_COLOR[action.status]} style={{ flexShrink: 0 }}>
            {action.status === 'DONE' ? 'Eingereicht' : ACTION_STATUS_LABEL[action.status]}
          </Badge>
        </Group>

        <Group gap="xs" wrap="wrap">
          <KbScopeBadges customers={customers} size="xs" />
          <Badge size="xs" variant="outline" color="gray">
            {article.category}
          </Badge>
          {article.tags.map((t) => (
            <Badge key={t} size="xs" variant="dot" color="gray">
              {t}
            </Badge>
          ))}
        </Group>
        <Text size="xs" c="dimmed">
          Fassung {article.revision} · {byLine}
        </Text>

        {errors.length || hints.length ? (
          <Stack gap={4}>
            {[...errors, ...hints].map((issue, i) => (
              <Stack key={i} gap={2}>
                <Text size="xs" c={issue.level === 'error' ? 'red' : issue.level === 'warning' ? 'yellow.8' : 'dimmed'}>
                  {issue.message}
                </Text>
                {open && issue.field === 'customer' && issue.candidates?.length ? (
                  <Group gap={4} wrap="wrap">
                    {issue.candidates.map((c) => (
                      <Button key={c.id} size="compact-xs" variant="light" disabled={busy} onClick={() => pickCustomer(issue, c.id)}>
                        {c.label}
                        {c.hint ? ` (${c.hint})` : ''}
                      </Button>
                    ))}
                  </Group>
                ) : null}
              </Stack>
            ))}
            {resolved?.similar.length ? (
              <Group gap={6} wrap="wrap">
                {resolved.similar.map((s) => (
                  <Anchor key={s.id} component={Link} to={`/knowledge-base/${encodeURIComponent(s.slug)}`} size="xs">
                    {s.title}
                  </Anchor>
                ))}
              </Group>
            ) : null}
          </Stack>
        ) : null}

        {action.status === 'FAILED' && item.error ? (
          <Alert color="red" variant="light" p="xs">
            <Text size="xs">{item.error}</Text>
          </Alert>
        ) : null}

        <Box
          style={{
            position: 'relative',
            maxHeight: showAll ? undefined : compact ? 220 : 320,
            overflow: 'hidden',
            borderTop: '1px solid var(--mantine-color-default-border)',
            paddingTop: 4,
          }}
        >
          <KnowledgeBaseMarkdown content={article.content || '_Noch kein Text._'} />
        </Box>
        <Button variant="subtle" size="compact-xs" onClick={() => setShowAll((v) => !v)} style={{ alignSelf: 'flex-start' }}>
          {showAll ? 'Kürzer anzeigen' : 'Ganze Anleitung anzeigen'}
        </Button>

        {action.status === 'RUNNING' ? (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="xs" c="dimmed">
              Wird eingereicht …
            </Text>
          </Group>
        ) : null}

        {open ? (
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Group gap="xs">
              <Button
                size="xs"
                color="cyberOrange"
                leftSection={<IconSend size={14} />}
                loading={execute.isPending}
                disabled={busy || errors.length > 0}
                onClick={submit}
              >
                {action.status === 'FAILED' ? 'Erneut einreichen' : executeLabel(action, 1)}
              </Button>
              <Button size="xs" variant="light" leftSection={<IconEdit size={14} />} disabled={busy} onClick={() => setEditing(true)}>
                Bearbeiten
              </Button>
            </Group>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<IconTrash size={14} />}
              disabled={busy}
              onClick={() => setConfirmDiscard(true)}
            >
              Verwerfen
            </Button>
          </Group>
        ) : null}
        {open ? (
          <Text size="xs" c="dimmed">
            Noch nichts eingereicht. Verbessern: im Chat sagen, was anders sein soll („Schritt 3 genauer", „nur für diesen
            Kunden") — oder selbst bearbeiten. Nach dem Einreichen gibt ein Admin die Anleitung frei.
          </Text>
        ) : null}
        {pending && readOnly ? (
          <Text size="xs" c="dimmed">
            Das Gespräch ist archiviert. Zum Einreichen oder Ändern erst wieder aktivieren.
          </Text>
        ) : null}
        {action.status === 'DONE' ? (
          <Group gap="xs" wrap="wrap">
            <Text size="xs" c="dimmed">
              Ein Admin prüft sie unter Admin → Wissensdatenbank. Du bekommst Bescheid, sobald sie freigegeben oder
              zurückgegeben ist.
            </Text>
            {articleHref ? (
              <Button
                component={Link}
                to={articleHref}
                size="compact-xs"
                variant="light"
                rightSection={<IconExternalLink size={12} />}
              >
                Anleitung öffnen
              </Button>
            ) : null}
          </Group>
        ) : null}
      </Stack>

      <KianKnowledgeArticleEditorModal
        opened={editing}
        onClose={() => setEditing(false)}
        article={article}
        customers={resolvedCustomers}
        saving={update.isPending}
        onSave={(draft) => patch(draft, () => setEditing(false))}
      />

      <Modal opened={confirmDiscard} onClose={() => setConfirmDiscard(false)} title="Entwurf verwerfen?" centered size="sm" zIndex={5300}>
        <Stack gap="sm">
          <Text size="sm">Die Anleitung wird verworfen. In der Wissensdatenbank steht nichts davon.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDiscard(false)}>
              Behalten
            </Button>
            <Button
              color="red"
              loading={discard.isPending}
              onClick={() =>
                discard.mutate(undefined, {
                  onSuccess: () => setConfirmDiscard(false),
                  onError: (err) => notifications.show({ color: 'red', title: 'Nicht verworfen', message: errorMessage(err) }),
                })
              }
            >
              Verwerfen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
